import type { VaultFileAccount } from '@/store/accountsApi';
import type {
  Document2Record,
  DocumentFolderRecord,
  DocumentRecord,
  HistoricalTaxExternalFile,
} from '@/store/documentsApi';
import { isRecord } from '@/utils/apiPayload';
import { resolveFilenameForMimeLookup } from '@/utils/fileValidation';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';

export function hasActiveDocumentVaultTag(
  showTagSelectorDropdown: boolean,
  selection: ViewingTagSelection,
  advisorInvestorId?: number,
): boolean {
  return (
    showTagSelectorDropdown &&
    selection !== null &&
    advisorInvestorId === undefined
  );
}

interface SelectedTagEmptyStateArgs {
  hasSelectedTag: boolean;
  searchTerm?: string;
  isLoading: boolean;
  isFetching: boolean;
  rowCount: number;
  isQuerySkipped?: boolean;
  isAccountView?: boolean;
  accountCount?: number;
}

export function shouldShowDocumentVaultTagEmptyState({
  hasSelectedTag,
  searchTerm,
  isLoading,
  isFetching,
  rowCount,
  isQuerySkipped = false,
  isAccountView = false,
  accountCount,
}: SelectedTagEmptyStateArgs): boolean {
  if (
    !hasSelectedTag ||
    isLoading ||
    isFetching ||
    isQuerySkipped ||
    Boolean(searchTerm)
  ) {
    return false;
  }

  if (!isAccountView && accountCount === 0) {
    return true;
  }

  return rowCount === 0;
}

export const isVaultFileAccount = (
  value: unknown,
): value is VaultFileAccount => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.WealthAccountId === 'number' &&
    typeof value.Name === 'string' &&
    typeof value.AccountNumber === 'string'
  );
};

export const normalizeVaultFileAccounts = (
  value: unknown,
): VaultFileAccount[] =>
  Array.isArray(value) ? value.filter(isVaultFileAccount) : [];

export const isDocument2Record = (value: unknown): value is Document2Record => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.VaultFile2Id === 'number' &&
    typeof value.Name === 'string' &&
    typeof value.UploadDate === 'string'
  );
};

/**
 * Personal Storage files arrive merged into the Documents2 list with category
 * id 0, which no feed document uses.
 */
export const isPersonalStorageDocument2 = (
  doc: Pick<Document2Record, 'VaultFileCategoryId'>,
): boolean => doc.VaultFileCategoryId === 0;

/**
 * FCB historical tax statements are read live from FCB, not stored. Every one
 * arrives with this `VaultFile2Id`, and its `VendorFileId` names the statement.
 */
export const HISTORICAL_TAX_VAULT_FILE2_ID = -5;

export const getHistoricalTaxStatementId = (
  doc: Pick<Document2Record, 'VaultFile2Id' | 'VendorFileId'>,
): string | undefined =>
  doc.VaultFile2Id === HISTORICAL_TAX_VAULT_FILE2_ID && doc.VendorFileId
    ? doc.VendorFileId
    : undefined;

/**
 * Personal Storage files reuse their own table's id as `VaultFile2Id`, so it can
 * equal the id of a feed document. Their row id is negated to keep both sources
 * unique in one grid.
 */
const getDocument2RowId = (doc: Document2Record): number | string => {
  const statementId = getHistoricalTaxStatementId(doc);
  if (statementId) {
    return statementId;
  }
  return isPersonalStorageDocument2(doc) ? -doc.VaultFile2Id : doc.VaultFile2Id;
};

export const normalizeDocument2Records = (value: unknown): Document2Record[] =>
  Array.isArray(value)
    ? value.filter(isDocument2Record).map((doc) => ({
        ...doc,
        id: getDocument2RowId(doc),
      }))
    : [];

export const isDocumentRecord = (value: unknown): value is DocumentRecord => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.VaultFileId === 'string' &&
    typeof value.Name === 'string' &&
    typeof value.IsFolder === 'boolean'
  );
};

export const normalizeDocumentRecords = (value: unknown): DocumentRecord[] => {
  if (!isRecord(value) || !Array.isArray(value.Records)) {
    return [];
  }
  return value.Records.filter(isDocumentRecord);
};

export const skeletonRows = Array.from({ length: 5 }, (_, index) => index);

export function getDocumentFilenameForBlobHandling(
  doc: Document2Record | DocumentRecord,
): string {
  if (isDocument2Record(doc)) {
    return resolveFilenameForMimeLookup(doc.Name, {
      fileName: doc.FileName,
      extension: doc.Extension,
    });
  }

  return resolveFilenameForMimeLookup(doc.Name, {
    extension: doc.Extension,
  });
}

/**
 * The name a downloaded file gets: the File column name with its extension.
 * The stored file name can be a generated id, so it only supplies a missing
 * extension.
 */
export function getDocumentDownloadName(
  doc: Document2Record | DocumentRecord,
): string {
  const storedName = isDocument2Record(doc) ? doc.FileName : undefined;
  const storedExtension = storedName?.includes('.')
    ? storedName.slice(storedName.lastIndexOf('.') + 1)
    : undefined;
  return resolveFilenameForMimeLookup(doc.Name, {
    extension: doc.Extension || storedExtension,
  });
}

export const filterFoldersForMove = (
  folders: DocumentFolderRecord[],
  blockedIds: Set<number>,
): DocumentFolderRecord[] =>
  folders
    .filter((folder) => !blockedIds.has(Number(folder.VaultFileId)))
    .map((folder) => ({
      ...folder,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Children is typed as a required array, but the folders API can omit it for leaf nodes.
      Children: filterFoldersForMove(folder.Children ?? [], blockedIds),
    }));

export const sortPersonalStorageRows = <
  T extends { Name: string; IsFolder: boolean },
>(
  rows: T[],
): T[] =>
  [...rows].sort((a, b) => {
    if (a.IsFolder !== b.IsFolder) {
      return a.IsFolder ? -1 : 1;
    }
    return a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' });
  });

/** The text the server sends in `Message` when a folder name is taken. */
export const DUPLICATE_FOLDER_SERVER_MESSAGE =
  'A folder with this name already exists.';

export const getErrorRecord = (error: unknown): Record<string, unknown> =>
  isRecord(error) ? error : {};

// Some endpoints hand back the error body as text, so a JSON text body is read too.
const readMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string' && data.trim().startsWith('{')) {
    try {
      return readMessage(JSON.parse(data.trim()));
    } catch {
      return undefined;
    }
  }
  if (!isRecord(data)) {
    return undefined;
  }
  const { message, Message } = data;
  if (typeof message === 'string') {
    return message;
  }
  return typeof Message === 'string' ? Message : undefined;
};

/**
 * The reason the server sent in `data.message` or `data.Message` (as an object
 * or as JSON text), or
 * `undefined` when it is missing or blank (a 500 can carry `{"Message": ""}`).
 */
export const getServerMessage = (error: unknown): string | undefined => {
  const message = readMessage(getErrorRecord(error).data);
  return message?.trim() ? message : undefined;
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
  const record = getErrorRecord(error);
  const serverMessage = getServerMessage(error);
  if (serverMessage) {
    return serverMessage;
  }
  if (readMessage(record.data) !== undefined) {
    return fallback;
  }
  if (typeof record.error === 'string' && record.error.length > 0) {
    return record.error;
  }
  if (typeof record.data === 'string' && record.data.trim().length > 0) {
    const trimmed = record.data.trim();
    return trimmed.length > 160 ? `${trimmed.slice(0, 160)}…` : trimmed;
  }
  if (typeof record.status === 'number') {
    return `HTTP ${String(record.status)}`;
  }
  return fallback;
};

export enum FileType {
  Image = 'image',
  PDF = 'pdf',
  Other = 'other',
}

export const getFileType = (extension?: string | null): FileType => {
  if (!extension) return FileType.Other;
  const ext = extension.toLowerCase().replace('.', '');
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext))
    return FileType.Image;
  if (ext === 'pdf') return FileType.PDF;
  return FileType.Other;
};

export const isPreviewableExtension = (extension?: string | null) =>
  getFileType(extension) !== FileType.Other;

const withCopyNumber = (filename: string, copy: number): string => {
  const dot = filename.lastIndexOf('.');
  return dot > 0
    ? `${filename.slice(0, dot)} (${String(copy)})${filename.slice(dot)}`
    : `${filename} (${String(copy)})`;
};

/**
 * The historical tax statements of a zip request. The server writes each one
 * into the zip under its name and does not number repeated names, and FCB gives
 * statements of one account and year the same name.
 */
export function toHistoricalTaxExternalFiles(
  docs: Document2Record[],
): HistoricalTaxExternalFile[] {
  const copies = new Map<string, number>();
  const usedNames = new Set<string>();
  const nameWithCopy = (name: string, copy: number) =>
    copy === 1 ? name : withCopyNumber(name, copy);
  return docs.flatMap((doc) => {
    const statementId = getHistoricalTaxStatementId(doc);
    if (!statementId) {
      return [];
    }
    const name = getDocumentDownloadName(doc);
    let copy = (copies.get(name) ?? 0) + 1;
    while (usedNames.has(nameWithCopy(name, copy))) {
      copy += 1;
    }
    copies.set(name, copy);
    const uniqueName = nameWithCopy(name, copy);
    usedNames.add(uniqueName);
    return [
      {
        externalFileId: statementId,
        externalFileName: uniqueName,
      },
    ];
  });
}

interface RowSelectionState {
  type: 'include' | 'exclude';
  ids: Set<string | number>;
}

export function getExplicitSelectedIds<T>(
  model: RowSelectionState,
  rows: T[],
  getRowId: (row: T) => string | number,
): Array<string | number> {
  if (model.type === 'exclude') {
    return rows.map(getRowId).filter((id) => !model.ids.has(id));
  }
  return Array.from(model.ids);
}
